import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import API from '@/lib/api';
import { DollarSign, Users, Calendar, ChefHat, ArrowRight, Clock, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function ChefDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [pendingBookings, setPendingBookings] = useState([]);

  useEffect(() => {
    API.get('/dashboard/stats').then(r => setStats(r.data)).catch(() => {});
    API.get('/in-home-bookings').then(r => {
      setPendingBookings(r.data.filter(b => b.status === 'pending').slice(0, 5));
    }).catch(() => {});
  }, []);

  const revenue = stats?.revenue;

  return (
    <div className="fade-in space-y-10" data-testid="chef-dashboard">
      <div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#222] font-[Manrope]">
          Chef Dashboard
        </h1>
        <p className="mt-2 text-[#717171] text-lg">Welcome back, Chef {user?.name?.split(' ')[0]}</p>
      </div>

      {/* Revenue Cards */}
      {revenue && (
        <div className="grid sm:grid-cols-4 gap-4">
          {[
            { label: 'Total Revenue', value: `$${(revenue.total || 0).toLocaleString()}`, icon: DollarSign, accent: 'text-[#E05A47] bg-[#E05A47]/10' },
            { label: 'Meal Plans', value: `$${(revenue.meal_plans || 0).toLocaleString()}`, icon: ChefHat, accent: 'text-blue-600 bg-blue-50' },
            { label: 'In-Home', value: `$${(revenue.in_home_bookings || 0).toLocaleString()}`, icon: Users, accent: 'text-green-600 bg-green-50' },
            { label: 'Events', value: `$${(revenue.events || 0).toLocaleString()}`, icon: Calendar, accent: 'text-purple-600 bg-purple-50' },
          ].map(({ label, value, icon: Icon, accent }) => (
            <div key={label} className="bg-white rounded-2xl border border-[#EBEBEB] p-6" data-testid={`revenue-${label.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold uppercase tracking-wide text-[#717171]">{label}</span>
                <div className={`w-8 h-8 rounded-lg ${accent} flex items-center justify-center`}>
                  <Icon className="w-4 h-4" strokeWidth={1.5} />
                </div>
              </div>
              <p className="text-2xl font-extrabold text-[#222] font-[Manrope]">{value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Overview Cards */}
      {stats && (
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="bg-[#F7F7F7] rounded-2xl p-6">
            <h3 className="text-sm font-semibold text-[#717171] uppercase tracking-wide mb-2">Meal Plans</h3>
            <p className="text-3xl font-extrabold text-[#222] font-[Manrope]">{stats.meal_plans?.active || 0}</p>
            <p className="text-sm text-[#717171] mt-1">Active of {stats.meal_plans?.total || 0} total</p>
          </div>
          <div className="bg-[#F7F7F7] rounded-2xl p-6">
            <h3 className="text-sm font-semibold text-[#717171] uppercase tracking-wide mb-2">In-Home Bookings</h3>
            <p className="text-3xl font-extrabold text-[#222] font-[Manrope]">{stats.in_home_bookings?.pending || 0}</p>
            <p className="text-sm text-[#717171] mt-1">Pending of {stats.in_home_bookings?.total || 0} total</p>
          </div>
          <div className="bg-[#F7F7F7] rounded-2xl p-6">
            <h3 className="text-sm font-semibold text-[#717171] uppercase tracking-wide mb-2">Events</h3>
            <p className="text-3xl font-extrabold text-[#222] font-[Manrope]">{stats.events?.total || 0}</p>
            <p className="text-sm text-[#717171] mt-1">{stats.events?.total_bookings || 0} total bookings</p>
          </div>
        </div>
      )}

      {/* Pending Bookings */}
      {pendingBookings.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-[#E05A47]" strokeWidth={1.5} />
              <h2 className="text-xl font-bold text-[#222] font-[Manrope]">Pending Approvals</h2>
            </div>
            <Link to="/chef/bookings" className="text-sm text-[#E05A47] font-semibold flex items-center gap-1 hover:underline">
              View all <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="space-y-3">
            {pendingBookings.map(b => (
              <div key={b.id} className="bg-white rounded-xl border border-[#EBEBEB] p-4 flex items-center justify-between" data-testid={`pending-booking-${b.id}`}>
                <div>
                  <p className="font-bold text-sm text-[#222] font-[Manrope]">{b.user_name}</p>
                  <p className="text-xs text-[#717171] mt-0.5 flex items-center gap-1.5">
                    <span className="capitalize">{b.booking_type?.replace('-', ' ')}</span>
                    <span>·</span>
                    <Clock className="w-3 h-3" strokeWidth={1.5} />
                    {new Date(b.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at {b.time}
                    <span>·</span>
                    {b.guests} guests
                  </p>
                </div>
                <Link to="/chef/bookings">
                  <Button size="sm" className="bg-[#E05A47] hover:bg-[#C54A39] text-white rounded-lg text-xs btn-press">Review</Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Links */}
      <div className="grid sm:grid-cols-3 gap-4">
        {[
          { to: '/chef/bookings', label: 'Manage Bookings', icon: Users },
          { to: '/chef/events', label: 'Manage Events', icon: Calendar },
          { to: '/chef/grocery-list', label: 'Grocery List', icon: ChefHat },
        ].map(({ to, label, icon: Icon }) => (
          <Link key={to} to={to} className="bg-white rounded-xl border border-[#EBEBEB] p-5 flex items-center gap-4 card-hover" data-testid={`quick-link-${label.toLowerCase().replace(/\s+/g, '-')}`}>
            <Icon className="w-5 h-5 text-[#E05A47]" strokeWidth={1.5} />
            <span className="font-semibold text-[#222] text-sm">{label}</span>
            <ArrowRight className="w-4 h-4 text-[#717171] ml-auto" />
          </Link>
        ))}
      </div>
    </div>
  );
}
